import Container from 'typedi'
import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import LearningService from '../services/learning.service'
import {
    Course,
    LmsSettings,
    Chapter,
    Content,
    ChapterPayload,
    CoursePayload,
    GetContentPayload,
    CourseContentsChapter,
    ParticipantCourseProgress,
    CourseContentsContentWithProgress,
    CourseContentsChapterWithProgress,
    GradeSurveyPayload, StartSurveyPayload, QuizResult
} from '@/modules/learning/learning.model'

@Module({
    namespaced: true
})
export default class LearningStore extends VuexModule {
    // keeping the two types separate here. To serve as caching almost.
    lms_settings: LmsSettings | null = null
    lesson: Content | null = null
    assessment: Content | null = null
    table_of_contents: Array<unknown> = []
    chapter_contents: Array<Content> | null = null
    current_chapter: Chapter | null = null
    course: Course | null = null
    chapters: Array<Chapter> = []
    loading_content = false

    get courseContentsWithProgress () {
        if (!Array.isArray(this.table_of_contents)) {
            return []
        }

        const participantCourseProgress: ParticipantCourseProgress = (this as any).context.rootState.auth?.participant?.participant_data?.course_progress
        const toc: CourseContentsChapterWithProgress[] = []
        this.table_of_contents.forEach((originalChapter: CourseContentsChapter) => {
            const totalContents = originalChapter.contents.length
            let completedContents = 0
            const chapter: CourseContentsChapterWithProgress = {
                ...structuredClone(originalChapter),
                completed: false,
                percent_completed: 0,
                contents: []
            }

            chapter.contents = originalChapter.contents.map((content) => {
                const contentWithProgress: CourseContentsContentWithProgress = {
                    ...content,
                    available: content.prerequisites?.every((prerequisite_content_id: string) => {
                        return participantCourseProgress?.['content_progress']?.[prerequisite_content_id]?.completed
                    }) ?? false,
                    participant_progress: participantCourseProgress?.['content_progress']?.[content.page_name] ?? {}
                }

                if (contentWithProgress.participant_progress?.completed) {
                    completedContents++
                }

                return contentWithProgress
            })

            chapter.completed = completedContents === totalContents && totalContents > 0
            chapter.percent_completed = totalContents > 0 ? Math.round((completedContents / totalContents) * 100) : 0
            toc.push(chapter)
        })
        return toc
    }

    get availableContent (): Array<string> {
        if (this.chapter_contents) {
            return this.chapter_contents.map(({ page_name}) => {
                return page_name
            })
        } else {
            return []
        }
    }

    @Mutation
    SET_LMS_SETTINGS (payload: any) {
        this.lms_settings = payload
    }

    @Mutation
    RESET_CONTENT () {
        this.lesson = null
        this.assessment = null
    }

    @Mutation
    SET_CONTENT_LOADING (payload: boolean) {
        this.loading_content = payload
    }

    @Mutation
    SET_LESSON(payload: Content) {
        this.lesson = payload
    }

    @Mutation
    SET_ASSESSMENT (payload: Content) {
        this.assessment = payload
    }

    @Mutation
    SET_TABLE_OF_CONTENTS (payload: any) {
        this.table_of_contents = payload
    }

    @Mutation
    SET_CURRENT_CHAPTER (uuid: string) {
        const chapter = this.chapters.find(chapter => chapter.uuid === uuid)
        this.current_chapter = chapter as Chapter
    }

    @Mutation
    SET_CHAPTERS (payload: Array<Chapter>) {
        this.chapters = payload
    }

    @Mutation
    SET_CHAPTER (payload: any) {
        this.chapter_contents = payload
    }

    @Mutation
    SET_COURSE (payload: any) {
        this.course = payload
    }

    @Action({ rawError: true })
    async getContent ({ course_uuid, chapter_id, content_id }: GetContentPayload) {
        const response = await Container.get(LearningService).getContent(course_uuid, chapter_id, content_id)

        if (content_id.startsWith('lms_assessment_')) {
            this.context.commit('SET_ASSESSMENT', response)
        } else {
            this.context.commit('SET_LESSON', response)
        }
    }

    @Action({ rawError: true })
    async getAttempts ({ course_uuid, content_id }: GetContentPayload): Promise<number> {
        const { data: attempts } = await Container.get(LearningService).getAttempts(course_uuid, content_id)
        return attempts
    }

    @Action({ rawError: true })
    async getTableOfContents({ course_uuid }: CoursePayload) {
        const response = await Container.get(LearningService).getTableOfContents(course_uuid)
        this.context.commit('SET_TABLE_OF_CONTENTS', response.data)
        return response.data
    }

    @Action({ rawError: true })
    async getChapters ({ course_uuid }: CoursePayload) {
        const response = await Container.get(LearningService).getChapters(course_uuid)
        this.context.commit('SET_CHAPTERS', response)
    }

    @Action({ rawError: true })
    async getChapterContent ({ course_uuid, chapter_uuid }: ChapterPayload) {
        const response = await Container.get(LearningService).getChapter(course_uuid, chapter_uuid)
        this.context.commit('SET_CHAPTER', response)
    }

    @Action({ rawError: true })
    async getCourse ({ course_uuid }: CoursePayload) {
        const response = await Container.get(LearningService).getCourse(course_uuid)
        this.context.commit('SET_COURSE', response.data)
        const settings = response.data.company.lms_settings || null
        this.context.commit('SET_LMS_SETTINGS', settings)
    }

    @Action({ rawError: true })
    async saveProgress ({ course_uuid, chapter_uuid, assessment, result_uuid, body }: GradeSurveyPayload): Promise<{ passed: boolean; score: number }> {
        const { data } = await Container.get(LearningService).saveProgress(course_uuid, chapter_uuid, assessment, result_uuid, body)
        return data
    }

    @Action({ rawError: true })
    async submitSurveyAnswer ({ course_uuid, chapter_uuid, assessment, result_uuid, body }: GradeSurveyPayload): Promise<{ passed: boolean; score: number }> {
        const { data } = await Container.get(LearningService).submitSurveyAnswer(course_uuid, chapter_uuid, assessment, result_uuid, body)
        return data
    }

    @Action({ rawError: true })
    async startSurvey ({ course_uuid, chapter_uuid, assessment }: StartSurveyPayload): Promise<{ attempt_details: QuizResult; time_left: number | null }> {
        const { data } = await Container.get(LearningService).startQuiz(course_uuid, chapter_uuid, assessment)
        return data
    }
}
